<!-- src/views/timesheets/TimesheetEntryView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    <FormHeader />
    
    <div class="p-4 sm:p-6 md:p-8 no-print">
      <div class="max-w-7xl mx-auto pb-24">
        
        <!-- BRANDED SUCCESS NOTIFICATION -->
        <div v-if="successMessage" class="mb-10 p-10 bg-[#EBFDF8] border-2 border-[#20FCF1]/20 rounded-[40px] shadow-2xl animate-bounce text-left">
          <div class="flex flex-col md:flex-row items-center gap-8 text-center md:text-left">
            <div class="bg-[#20FCF1] p-5 rounded-[32px] shadow-lg text-[#0A1630]">
              <CheckBadgeIcon class="h-12 w-12" />
            </div>
            <div>
              <h2 class="text-3xl font-black text-[#0A1630] uppercase tracking-tight leading-none">Submission Authorised</h2>
              <p class="text-sm font-bold text-[#20FCF1] uppercase tracking-[0.2em] mt-3">{{ successMessage }}</p>
            </div>
          </div>
        </div>

        <!-- MAIN FORM INTERFACE -->
        <form v-if="!isSubmitting && !successMessage" @submit.prevent="submitTimesheet" class="space-y-10 animate-fadeIn text-left">
          
          <!-- MASTER HEADER (INTEGRATED MINT ANCHOR) -->
          <div class="bg-[#0A1630] rounded-[40px] p-8 md:p-12 border-b-4 border-[#20FCF1] shadow-2xl relative overflow-hidden text-left border-x border-t border-slate-800">
            <div class="absolute top-0 right-0 p-12 opacity-5 pointer-events-none"><ClockIcon class="h-32 w-32 text-white" /></div>
            
            <div class="relative z-10 flex flex-col md:flex-row justify-between items-center gap-8">
              <div>
                <h1 class="text-3xl md:text-4xl font-black text-white uppercase tracking-tight leading-none text-left">Time Tracker</h1>
                <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-4 text-left">
                  Personnel: {{ userStore.userProfile?.firstName }} {{ userStore.userProfile?.lastName }} • {{ userStore.userProfile?.department || 'General Operations' }}
                </p>
              </div>

              <div class="flex flex-wrap items-center justify-center gap-4">
                <button type="button" @click="isGuideOpen = true" class="px-6 py-3 bg-white/5 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/10 transition-all flex items-center gap-2">
                  <InformationCircleIcon class="h-4 w-4 text-[#20FCF1]" /> Guide Protocol
                </button>

                <div class="flex items-center gap-3">
                  <button type="button" @click="changeWeek(-1)" class="p-3 bg-white/5 border border-white/10 rounded-2xl text-white hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all">
                    <ChevronLeftIcon class="h-5 w-5" />
                  </button>
                  <div class="bg-white/5 px-8 py-3 rounded-2xl border border-white/10 text-center min-w-[140px]">
                    <span class="block text-[9px] font-black text-[#FD5E00] uppercase tracking-widest mb-1">Rail Week</span>
                    <span class="block text-2xl font-black text-white leading-none">{{ currentRailWeekDisplay }}</span>
                  </div>
                  <button type="button" @click="changeWeek(1)" class="p-3 bg-white/5 border border-white/10 rounded-2xl text-white hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all">
                    <ChevronRightIcon class="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- CORRECTION PROTOCOL ALERT -->
          <div v-if="correctionData" class="bg-indigo-50 border-2 border-indigo-100 p-8 rounded-[40px] shadow-sm animate-fadeIn flex flex-col md:flex-row items-start md:items-center gap-6 text-left">
            <div class="bg-indigo-600 p-4 rounded-2xl text-white shadow-lg flex-shrink-0">
                <ExclamationCircleIcon class="h-6 w-6" />
            </div>
            <div class="text-left">
                <h4 class="text-sm font-black text-indigo-900 uppercase tracking-widest text-left">Correction Handshake Active</h4>
                <p class="text-xs font-medium text-indigo-700 mt-1 text-left">Registry pre-filled for Week {{ correctionData.railWeek }}. Address management feedback before resubmitting.</p>
                <p v-if="correctionData.queryNotes" class="mt-3 text-[11px] bg-white/50 p-4 rounded-xl border border-indigo-100 italic text-indigo-600 font-bold leading-relaxed text-left">
                  "{{ correctionData.queryNotes }}"
                </p>
            </div>
          </div>

          <!-- GLOBAL SYNC MODULE -->
          <div class="bg-white p-8 rounded-[40px] border border-slate-100 shadow-xl text-left">
              <div class="flex items-center justify-between mb-8">
                <h2 class="text-[10px] font-black text-[#0A1630] uppercase tracking-[0.3em]">Master Protocol Synchronisation</h2>
                <div v-if="hasDraft" class="flex items-center gap-2 px-3 py-1 bg-[#20FCF1]/10 text-[#20FCF1] rounded-lg">
                  <div class="w-1.5 h-1.5 bg-[#20FCF1] rounded-full animate-pulse"></div>
                  <span class="text-[8px] font-black uppercase tracking-widest">Local Draft Active</span>
                </div>
              </div>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
                <div class="space-y-3 text-left">
                  <label class="form-label !mb-0 ml-1 text-left">Weekly Contract / Job Ref Selection</label>
                  <div class="flex gap-3 text-left">
                    <div class="flex-grow text-left">
                      <VueMultiselect
                        v-model="selectedGlobalJob"
                        :options="jobOptions"
                        :searchable="true" :close-on-select="true" :show-labels="false"
                        label="text" track-by="value" placeholder="Search Job Reference..."
                        class="premium-multiselect-main"
                      />
                    </div>
                    <div class="bg-[#0A1630] px-5 rounded-2xl flex items-center justify-center text-[#20FCF1] shadow-lg">
                      <LinkIcon class="h-5 w-5" />
                    </div>
                  </div>
                </div>

                <div class="space-y-3 text-left">
                  <label class="form-label !mb-0 ml-1 text-left">Weekly Department Selection</label>
                  <div class="flex gap-3 text-left">
                    <div class="flex-grow text-left">
                      <VueMultiselect
                        v-model="selectedGlobalDept"
                        :options="deptOptions"
                        :searchable="true" :close-on-select="true" :show-labels="false"
                        label="text" track-by="value" placeholder="Assign Department..."
                        class="premium-multiselect-main"
                      />
                    </div>
                    <div class="bg-[#0A1630] px-5 rounded-2xl flex items-center justify-center text-[#20FCF1] shadow-lg">
                      <BriefcaseIcon class="h-5 w-5" />
                    </div>
                  </div>
                </div>
              </div>
          </div>

          <!-- Daily Cards Grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 text-left">
            <div v-for="(day, dIdx) in timesheetData.days" :key="dIdx" class="bg-white border-2 rounded-[32px] p-6 transition-all duration-300 flex flex-col min-h-[520px]" :class="day.isOff ? 'border-slate-100 opacity-60 bg-slate-50' : 'border-slate-100 shadow-lg hover:border-[#20FCF1]'">
              <div class="flex justify-between items-start mb-8">
                <div class="text-left">
                  <h3 class="text-lg font-black text-[#0A1630] uppercase leading-none">{{ day.dayName }}</h3>
                  <p class="text-[10px] font-bold text-slate-400 mt-2 uppercase tracking-wider">{{ day.formattedDate }}</p>
                </div>
                <button type="button" @click="toggleRestDay(dIdx)" class="px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest transition-all border" :class="day.isOff ? 'bg-slate-200 text-slate-500 border-transparent' : 'bg-[#EBFDF8] text-[#20FCF1] border-[#20FCF1]/20'">
                  {{ day.isOff ? 'Off' : 'Worked' }}
                </button>
              </div>

              <div v-if="!day.isOff" class="space-y-6 flex-grow text-left">
                <div v-for="(entry, eIdx) in day.entries" :key="eIdx" class="p-5 bg-slate-50 rounded-2xl border border-slate-100 space-y-4 animate-fadeIn relative text-left">
                    <div class="space-y-1 text-left">
                      <label class="micro-label">Department</label>
                      <VueMultiselect
                        v-model="entry.multiselectDept"
                        :options="deptOptions"
                        :searchable="true" :close-on-select="true" :show-labels="false"
                        label="text" track-by="value" placeholder="Select Dept..."
                        @update:model-value="(val) => syncEntryDept(dIdx, eIdx, val)"
                        class="premium-multiselect-inline"
                      />
                    </div>

                    <div class="space-y-1 text-left">
                      <label class="micro-label">Job Reference</label>
                      <VueMultiselect
                        v-model="entry.multiselectRef"
                        :options="jobOptions"
                        :searchable="true" :close-on-select="true" :show-labels="false"
                        label="text" track-by="value" placeholder="Select Job..."
                        @update:model-value="(val) => syncEntryJob(dIdx, eIdx, val)"
                        class="premium-multiselect-inline"
                      />
                    </div>

                    <div class="space-y-1 text-left">
                      <label class="micro-label text-left">Task Description</label>
                      <input type="text" v-model="entry.description" class="form-input-small !bg-white text-left" placeholder="Specific activities..." />
                    </div>

                    <div class="flex items-center justify-between gap-4 text-left">
                      <div class="flex-grow space-y-1 text-left">
                        <label class="micro-label text-left">Logged Hours</label>
                        <input type="number" v-model.number="entry.hours" step="0.5" min="0" max="24" class="form-input-small !bg-white text-center" />
                      </div>
                      <button v-if="day.entries.length > 1" type="button" @click="removeEntry(dIdx, eIdx)" class="self-end mb-1 p-2.5 bg-rose-50 text-rose-500 rounded-xl hover:bg-rose-500 hover:text-white transition-all"><TrashIcon class="h-4 w-4" /></button>
                    </div>
                </div>

                <button type="button" @click="addEntry(dIdx)" class="w-full flex items-center justify-center gap-2 py-4 bg-[#0A1630] text-white rounded-2xl hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all group shadow-md text-center">
                  <PlusIcon class="h-4 w-4 text-[#20FCF1] group-hover:text-[#0A1630]" />
                  <span class="text-[9px] font-black uppercase tracking-widest">Append Job Registry</span>
                </button>

                <div class="mt-4 pt-5 border-t border-slate-100 flex justify-between items-center text-left">
                   <span class="text-[9px] font-black text-slate-400 uppercase tracking-widest text-left">Daily Log</span>
                   <div class="flex items-center gap-2 text-left">
                     <span class="text-2xl font-black text-[#0A1630] tracking-tighter">{{ calculateDailyTotal(day) }}</span>
                     <span class="text-[10px] font-black text-[#20FCF1] uppercase">Hrs</span>
                   </div>
                </div>
              </div>

              <div v-else class="flex-grow flex items-center justify-center py-10 opacity-30 text-center">
                <div class="text-center"><MoonIcon class="h-10 w-10 mx-auto mb-3" /><p class="text-[10px] font-black uppercase tracking-[0.2em] text-center">Rest Protocol</p></div>
              </div>
            </div>
          </div>

          <!-- Submission Module -->
          <div class="bg-[#0A1630] p-10 md:p-12 rounded-[48px] shadow-2xl flex flex-col md:flex-row items-center justify-between gap-10 border-t-8 border-[#20FCF1] animate-fadeIn text-left">
            <div class="text-center md:text-left">
              <p class="text-[#20FCF1] text-[10px] font-black uppercase tracking-[0.3em] mb-3 ml-1 text-left">Verified Periodic Total</p>
              <h4 class="text-6xl font-black text-white leading-none tracking-tighter text-left">{{ totalWeeklyHours }} <span class="text-xl text-white/20 font-bold uppercase tracking-widest ml-3">Hours</span></h4>
            </div>
            
            <div class="flex flex-col sm:flex-row gap-6 w-full md:w-auto text-center">
              <button type="button" @click="clearDraftAndReset" class="px-10 py-5 text-rose-400 hover:text-white font-black uppercase text-[10px] tracking-widest text-center transition-colors">Abort Draft</button>
              <button type="submit" :disabled="isSubmitting || totalWeeklyHours <= 0" class="px-16 py-6 bg-[#20FCF1] text-[#0A1630] rounded-[24px] font-black uppercase text-xs tracking-[0.2em] shadow-xl hover:scale-105 active:scale-95 transition-all disabled:opacity-50 text-center">Authorise & File Registry</button>
            </div>
          </div>
        </form>

        <div v-if="isSubmitting" class="text-center py-32 animate-fadeIn bg-white rounded-[40px] border-2 border-dashed border-slate-200 text-center">
            <div class="animate-spin h-16 w-16 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full mx-auto mb-8 text-center"></div>
            <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-[0.4em] text-center">Synchronising Authorisation Registry...</p>
        </div>
      </div>
    </div>

    <BottomGuidePanel :isOpen="isGuideOpen" title="Timesheet Command Protocol" @close="isGuideOpen = false">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-12 p-12 text-left">
            <div class="space-y-4 text-left">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center font-black shadow-lg text-lg">1</div>
                <h4 class="font-black text-[#0A1630] uppercase tracking-tight text-left">Independent Storage</h4>
                <p class="text-sm text-slate-500 font-medium font-sans text-left">Every Rail Week utilizes a unique encryption key for local drafts. Switching weeks will never overwrite your active session data.</p>
            </div>
            <div class="space-y-4 text-left">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center font-black shadow-lg text-lg">2</div>
                <h4 class="font-black text-[#0A1630] uppercase tracking-tight text-left">Hierarchy Escalation</h4>
                <p class="text-sm text-slate-500 font-medium font-sans text-left">If your Dept. Manager activates Absence Protocol or if you are a Manager yourself, requests automatically escalate to Dept. Directors.</p>
            </div>
            <div class="space-y-4 text-left">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center font-black shadow-lg text-xl">3</div>
                <h4 class="font-black text-[#0A1630] uppercase tracking-tight text-left">Registry Purge</h4>
                <p class="text-sm text-slate-500 font-medium font-sans text-left">Local data is only purged once a secure handshake is confirmed with the primary management command centre.</p>
            </div>
        </div>
    </BottomGuidePanel>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useUserStore } from '@/stores/userStore';
import { db, firebaseApp, auth } from '@/firebaseConfig';
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useRouter } from 'vue-router';
import { translateSentinelError } from '@/utils/sentinelErrorHandler';
import BottomGuidePanel from '@/components/common/BottomGuidePanel.vue';
import FormHeader from '@/components/FormHeader.vue';
import VueMultiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import { 
  ChevronLeftIcon, ChevronRightIcon, LinkIcon, MoonIcon,
  InformationCircleIcon, ClockIcon, CheckBadgeIcon, PlusIcon, TrashIcon,
  BriefcaseIcon, ExclamationCircleIcon
} from '@heroicons/vue/24/outline';

const userStore = useUserStore();
const router = useRouter();
const functions = getFunctions(firebaseApp, 'europe-west2');

const isSubmitting = ref(false);
const isGuideOpen = ref(false);
const isHydrated = ref(false);
const weekOffset = ref(0);
const globalJobNumber = ref('');
const globalDepartment = ref('');
const selectedGlobalJob = ref(null); 
const selectedGlobalDept = ref(null);
const currentRailWeekDisplay = ref('00');
const successMessage = ref('');
const jobOptions = ref([]);
const deptOptions = ref([]);
const hasDraft = ref(false);
const correctionData = ref(null);
const isInitialising = ref(false);

const timesheetData = reactive({
  clientId: '', userId: '', department: '', railWeek: 0, year: new Date().getFullYear(), days: []
});

const STORAGE_KEY = computed(() => {
    const uid = auth.currentUser?.uid || 'anon';
    const week = timesheetData.railWeek || '0';
    const year = timesheetData.year || new Date().getFullYear();
    return `sentinel_ts_draft_${uid}_W${week}_Y${year}`;
});

const fetchOptions = async () => {
  const cid = userStore.clientId || userStore.userProfile?.clientId;
  if (!cid) return;
  try {
    const [jobSnap, deptSnap] = await Promise.all([
      getDoc(doc(db, 'formOptions', 'jobReferences')),
      getDoc(doc(db, 'formOptions', 'departments'))
    ]);
    if (jobSnap.exists()) {
      const raw = jobSnap.data()[cid] || jobSnap.data().global || [];
      jobOptions.value = raw.sort((a, b) => (a.text || '').localeCompare(b.text || ''));
    }
    if (deptSnap.exists()) {
      const raw = deptSnap.data()[cid] || deptSnap.data().global || [];
      deptOptions.value = raw.sort((a, b) => (a.text || '').localeCompare(b.text || ''));
    }
  } catch (err) { console.error("Registry Fetch Failure:", err); }
};

const saveDraft = () => {
    if (!isHydrated.value || isSubmitting.value || isInitialising.value) return;
    localStorage.setItem(STORAGE_KEY.value, JSON.stringify({
        timesheetData: JSON.parse(JSON.stringify(timesheetData)),
        globalJobNumber: globalJobNumber.value,
        globalDepartment: globalDepartment.value
    }));
};

const loadRegistryData = async (week, year) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return false;
    try {
        const q = query(
            collection(db, 'timesheetSubmissions'),
            where('userId', '==', userId),
            where('formData.railWeek', '==', week),
            where('formData.year', '==', year),
            limit(1)
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
            const subDoc = snap.docs[0].data();
            const status = String(subDoc.formData?.status || '').toLowerCase();
            if (status.includes('queried') || status.includes('declined')) {
                const legacyData = subDoc.formData;
                Object.assign(timesheetData, legacyData);
                timesheetData.days.forEach(day => {
                    day.entries.forEach(entry => {
                        entry.multiselectRef = jobOptions.value.find(o => o.value === entry.jobNumber) || null;
                        entry.multiselectDept = deptOptions.value.find(o => o.value === entry.department) || null;
                    });
                });
                correctionData.value = { railWeek: week, queryNotes: legacyData.queryNotes || legacyData.rejectionNotes || null };
                return true;
            }
        }
    } catch (e) { console.error("Registry Retrieval Error", e); }
    return false;
};

const loadDraft = (week, year) => {
    const tempKey = `sentinel_ts_draft_${auth.currentUser?.uid || 'anon'}_W${week}_Y${year}`;
    const saved = localStorage.getItem(tempKey);
    if (!saved) return false;
    try {
        const parsed = JSON.parse(saved);
        Object.assign(timesheetData, parsed.timesheetData);
        globalJobNumber.value = parsed.globalJobNumber || '';
        selectedGlobalJob.value = jobOptions.value.find(o => o.value === globalJobNumber.value) || null;
        globalDepartment.value = parsed.globalDepartment || '';
        selectedGlobalDept.value = deptOptions.value.find(o => o.value === globalDepartment.value) || null;
        timesheetData.days.forEach(day => {
            day.entries.forEach(entry => {
                entry.multiselectRef = jobOptions.value.find(o => o.value === entry.jobNumber) || null;
                entry.multiselectDept = deptOptions.value.find(o => o.value === entry.department) || null;
            });
        });
        hasDraft.value = true;
        return true;
    } catch (e) { console.error("Draft Restoration Error", e); }
    return false;
};

const clearDraftAndReset = () => {
    if (!confirm("Authorise removal of current local draft?")) return;
    localStorage.removeItem(STORAGE_KEY.value);
    hasDraft.value = false;
    correctionData.value = null;
    selectedGlobalJob.value = null;
    globalJobNumber.value = '';
    selectedGlobalDept.value = null;
    globalDepartment.value = '';
    calculateDates(weekOffset.value);
};

const initSessionData = async () => {
  if (!auth.currentUser) return;
  const p = userStore.userProfile;
  const c = userStore.clientProfile;

  timesheetData.clientId = userStore.clientId || p?.clientId;
  // 🛡️ HARDENED: Fetching official Client Name to replace "The Company" placeholder
  timesheetData.clientName = c?.name || p?.clientName || 'TECS';
  timesheetData.userId = auth.currentUser.uid;
  
  /** 🛡️ CRITICAL ROUTING FIX
   * Restoring original casing for department matching. 
   * Forced lowercase was breaking the backend routing protocol.
   */
  timesheetData.department = p?.department || 'General';
  
  await fetchOptions();
  await calculateDates(0);
  isHydrated.value = true;
};

const calculateDates = async (offset = 0) => {
  isInitialising.value = true; 
  const now = new Date();
  now.setDate(now.getDate() + (offset * 7));
  const getStartSaturday = (year) => {
    let target = new Date(year, 2, 29);
    while (target.getDay() !== 6) target.setDate(target.getDate() + 1);
    return target;
  };
  const currentYear = now.getFullYear();
  let railYearStart = getStartSaturday(currentYear);
  if (now < railYearStart) railYearStart = getStartSaturday(currentYear - 1);
  const weekNum = Math.floor((now - railYearStart) / (7 * 24 * 60 * 60 * 1000)) + 1;
  currentRailWeekDisplay.value = weekNum.toString().padStart(2, '0');
  const targetYear = now.getFullYear();

  timesheetData.railWeek = weekNum;
  timesheetData.year = targetYear;

  selectedGlobalJob.value = null;
  globalJobNumber.value = '';
  selectedGlobalDept.value = null;
  globalDepartment.value = '';
  correctionData.value = null;
  hasDraft.value = false;

  const isCorrection = await loadRegistryData(weekNum, targetYear);
  if (isCorrection) { isInitialising.value = false; return; }
  if (loadDraft(weekNum, targetYear)) { isInitialising.value = false; return; }

  let startSat = new Date(now);
  startSat.setDate(now.getDate() - ((now.getDay() + 1) % 7));
  startSat.setHours(0, 0, 0, 0);
  const dayNames = ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  timesheetData.days = dayNames.map((name, index) => {
    const date = new Date(startSat);
    date.setDate(startSat.getDate() + index);
    return {
      dayName: name,
      formattedDate: date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
      isoDate: date.toISOString().split('T')[0],
      isOff: false,
      entries: [{ jobNumber: '', department: '', description: '', hours: 0, multiselectRef: null, multiselectDept: null }]
    };
  });
  isInitialising.value = false; 
};

watch(selectedGlobalJob, (newVal) => {
    if (isInitialising.value) return;
    const val = newVal ? newVal.value : '';
    globalJobNumber.value = val;
    timesheetData.days.forEach(day => {
        if (!day.isOff) { day.entries.forEach(entry => { entry.jobNumber = val; entry.multiselectRef = newVal; }); }
    });
});

watch(selectedGlobalDept, (newVal) => {
    if (isInitialising.value) return;
    const val = newVal ? newVal.value : '';
    globalDepartment.value = val;
    timesheetData.days.forEach(day => {
        if (!day.isOff) { day.entries.forEach(entry => { entry.department = val; entry.multiselectDept = newVal; }); }
    });
});

const syncEntryJob = (dIdx, eIdx, selectedObj) => { timesheetData.days[dIdx].entries[eIdx].jobNumber = selectedObj ? selectedObj.value : ''; };
const syncEntryDept = (dIdx, eIdx, selectedObj) => { timesheetData.days[dIdx].entries[eIdx].department = selectedObj ? selectedObj.value : ''; };
const addEntry = (dayIndex) => { timesheetData.days[dayIndex].entries.push({ jobNumber: globalJobNumber.value || '', department: globalDepartment.value || '', description: '', hours: 0, multiselectRef: selectedGlobalJob.value || null, multiselectDept: selectedGlobalDept.value || null }); };
const removeEntry = (dayIndex, entryIndex) => { if (timesheetData.days[dayIndex].entries.length > 1) timesheetData.days[dayIndex].entries.splice(entryIndex, 1); };
const toggleRestDay = (dayIndex) => { timesheetData.days[dayIndex].isOff = !timesheetData.days[dayIndex].isOff; };
const calculateDailyTotal = (day) => day.isOff ? 0 : day.entries.reduce((sum, entry) => sum + (Number(entry.hours) || 0), 0);
const changeWeek = (step) => { weekOffset.value += step; calculateDates(weekOffset.value); };
const totalWeeklyHours = computed(() => timesheetData.days.reduce((total, day) => total + calculateDailyTotal(day), 0));

watch([timesheetData, globalJobNumber, globalDepartment], () => { saveDraft(); }, { deep: true });

const submitTimesheet = async () => {
  if (totalWeeklyHours.value <= 0) { alert("Registry Blocked: No hours recorded."); return; }
  isSubmitting.value = true;
  
  try {
    const payload = {
      timesheetData: {
        ...timesheetData,
        totalHours: totalWeeklyHours.value,
        isCorrection: !!correctionData.value,
        status: 'Pending Authorisation'
      }
    };
    
    const res = await httpsCallable(functions, 'submitTimesheetWithRouting')(payload);
    
    if (res.data.success) {
      localStorage.removeItem(STORAGE_KEY.value);
      successMessage.value = `Registry Synchronised: ${res.data.referenceNumber}.`;
      setTimeout(() => { router.push({ name: 'dashboard' }); }, 3500);
    } else {
        throw new Error(res.data.message || "Handshake Rejected.");
    }
  } catch (err) { 
    isSubmitting.value = false; 
    // 🛡️ Applying Sentinel Signal Translation
    alert(translateSentinelError(err)); 
  }
};

onMounted(() => { 
  document.title = "Time Tracker | Sentinel Safe";
  if (userStore.isAuthReady) initSessionData(); 
});
</script>

<style scoped>
.form-label { @apply block text-[11px] font-black text-[#0A1630] uppercase tracking-[0.2em] mb-4 ml-1 text-left; }
.micro-label { @apply block text-[9px] font-black text-slate-400 uppercase tracking-[0.15em] mb-1.5 ml-1 text-left; }
.form-input-small { @apply block w-full px-4 py-3 border border-slate-100 rounded-xl text-[#0A1630] font-bold transition-all focus:ring-2 focus:ring-[#20FCF1] outline-none text-xs text-left; }
:deep(.multiselect__tags) { @apply border-2 border-slate-100 rounded-2xl bg-white min-h-[56px] pt-3 px-4 shadow-sm; }
:deep(.multiselect__placeholder) { @apply text-slate-400 font-bold text-sm; }
:deep(.multiselect__single) { @apply font-bold text-[#0A1630] text-sm mt-1; }
:deep(.multiselect__option--highlight) { @apply bg-[#20FCF1] text-[#0A1630]; }
:deep(.multiselect__option--selected) { @apply bg-[#0A1630] text-white; }
.premium-multiselect-inline :deep(.multiselect__tags) { @apply min-h-[48px] pt-2 rounded-xl border; }
.premium-multiselect-inline :deep(.multiselect__single) { @apply text-xs; }
.animate-fadeIn { animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
</style>